import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import logger from "../utils/logger";
import { getSocketUrl, getSocketOptions } from "../utils/socketConfig";

/**
 * Realtime sync for the unified task board, on the app's existing Socket.IO
 * server (same URL/options as the other socket hooks). Connected only while
 * the board page is mounted.
 *
 * After connecting, the client sends `taskboard:subscribe`; the SERVER decides
 * which task events this user may receive (admin / squad lead / own tasks).
 * Board state is never sent by the client over the socket — mutations go
 * through the REST API and the server publishes after persisting.
 *
 * Events handled:
 *   taskboard:task_created / task_updated → onTask(task, event)
 *   taskboard:task_removed                → onTaskRemoved(event)
 *   taskboard:task_activity               → onTaskActivity(event)  (comments, history, files)
 *   taskboard:catalog_changed             → onCatalogChanged(event)
 *   taskboard:access_changed              → onResync()  (memberships changed)
 *
 * On every RE-connection `onResync` is called so the board refetches whatever
 * was missed while offline (events are not replayed).
 *
 * @returns {"connecting" | "live" | "offline"} connection status for the UI
 */
export function useTaskBoardSocket({ token, onTask, onTaskRemoved, onTaskActivity, onCatalogChanged, onResync }) {
  const [status, setStatus] = useState("connecting");

  // Latest handlers via ref: listeners are registered once per connection, so
  // re-renders never stack duplicate listeners.
  const handlers = useRef({});
  handlers.current = { onTask, onTaskRemoved, onTaskActivity, onCatalogChanged, onResync };

  useEffect(() => {
    if (!token || !token.trim()) return undefined;

    const socket = io(
      getSocketUrl(),
      getSocketOptions(token, {
        // The board should keep trying for as long as it is open.
        reconnectionAttempts: Infinity,
      }),
    );
    let hasConnectedBefore = false;

    // Access tokens are short-lived: always reconnect with the freshest one
    // (useAxiosPrivate/refresh keeps localStorage up to date).
    const refreshAuth = () => {
      const latest = localStorage.getItem("accessToken");
      if (latest) socket.auth = { ...socket.auth, token: latest };
    };
    socket.io.on("reconnect_attempt", refreshAuth);

    socket.on("connect", () => {
      socket.emit("taskboard:subscribe", {}, (ack) => {
        if (!ack?.ok) {
          logger.error("[taskBoard] subscribe failed", ack?.error);
          setStatus("offline");
          return;
        }
        setStatus("live");
        if (hasConnectedBefore) handlers.current.onResync?.("reconnect");
        hasConnectedBefore = true;
      });
    });

    socket.on("disconnect", () => setStatus("offline"));
    socket.on("connect_error", (err) => {
      setStatus("offline");
      // Expired/invalid token: retry with the latest stored token.
      if (/token|auth/i.test(err?.message || "")) refreshAuth();
    });

    socket.on("taskboard:task_created", (e) => handlers.current.onTask?.(e.task, e));
    socket.on("taskboard:task_updated", (e) => handlers.current.onTask?.(e.task, e));
    socket.on("taskboard:task_removed", (e) => handlers.current.onTaskRemoved?.(e));
    socket.on("taskboard:task_activity", (e) => handlers.current.onTaskActivity?.(e));
    socket.on("taskboard:catalog_changed", (e) => handlers.current.onCatalogChanged?.(e));
    socket.on("taskboard:access_changed", () => handlers.current.onResync?.("access"));

    return () => {
      socket.emit("taskboard:unsubscribe");
      socket.disconnect();
    };
  }, [token]);

  return status;
}

export default useTaskBoardSocket;
